import { useState } from 'react';
import {
  postMailcomBulkDeleteAndRenew,
  postMailcomBulkRenew,
  postMailcomForceRenew,
  postMailcomRetryFailed,
  postMailcomRevive,
} from '../../api/endpoints.js';
import { useApiMutation } from '../../api/hooks.js';
import { Banner, Button, Card, Icon } from '../../ui/index.js';

const BULK_DELETE_STATUSES = ['active', 'suspended', 'login_failed', 'disabled'] as const;
const BULK_DELETE_LABELS: Record<(typeof BULK_DELETE_STATUSES)[number], string> = {
  active: 'Aktif',
  suspended: 'Alias Limiti / Suspended',
  login_failed: 'Giriş Hatası',
  disabled: 'Devre Dışı',
};
import { triggerExportAndDelete } from './ExportAndDeleteDialog.js';

// 2026-05-28 — operatör isteğiyle Export & Sil tüm statüler için açıldı.
// Eski kısıtlama (sadece login_failed/disabled) kaldırıldı. Stat filtresi
// herhangi bir şey olduğunda (Toplam dahil) buton render olur. Backend'de
// hard-delete + leased-alias guard var: aktif siparişli (leased) alias'lar
// varsa silme reddedilir, müşteri OTP'si bozulmaz.
const EXPORT_BUTTON_VISIBLE = (s?: string): boolean => typeof s === 'string' && s.length > 0;

interface MailcomToolbarProps {
  readonly providerId: string;
  readonly canManage: boolean;
  readonly canDrain: boolean;
  readonly onRefreshAll: () => void;
  readonly onOpenImport: () => void;
  readonly activeStatusFilter?: string;
  /** R-RAMBLER — `false` renders only Statü / Import / Yenile (no alias ops). */
  readonly aliasOps?: boolean;
}

export function MailcomToolbar({
  providerId,
  canManage,
  canDrain,
  onRefreshAll,
  onOpenImport,
  activeStatusFilter,
  aliasOps = true,
}: MailcomToolbarProps): JSX.Element {
  // 2026-06-06 — operatör isteğiyle "Toplu Yenileme" artık FORCE wipe+re-add
  // yapar: her parent için `available` + `used` aliasları siler (leased/reserved
  // ATLANIR — aktif sipariş koruması, D-186) ve 9 alias yeniden ürettirir.
  // Önceki davranış stoklu hesapları ATLIYORDU ("0 alındı / 125 atlandı");
  // force=true bunu kaldırır. Silme DB-tarafı yapılır → hesap exhausted olur →
  // Python renewal_worker mail.com'daki satırları siler + 9 mint eder (FR-1
  // koşullu wipe worker'da DEĞİŞMEZ). DESTRUCTIVE → provider:drain gerekir.
  const bulkRenew = useApiMutation((o, input: { confirm: boolean }) =>
    postMailcomBulkRenew(o, {
      confirm: input.confirm,
      force: true,
      onlyDepleted: false,
      statusFilter: ['active', 'suspended', 'login_failed', 'disabled'],
      limit: 5000,
    }, providerId),
  );
  const revive = useApiMutation((o, input: { confirm: boolean }) =>
    postMailcomRevive(o, { confirm: input.confirm, limit: 500 }, providerId),
  );
  const retryFailed = useApiMutation((o, input: { confirm: boolean }) =>
    postMailcomRetryFailed(o, { confirm: input.confirm, olderThanHours: 1, limit: 200 }, providerId),
  );
  const forceRenew = useApiMutation(
    (o, input: { accountId: string; reason: string; confirmationToken: string }) =>
      postMailcomForceRenew(o, input.accountId, {
        reason: input.reason,
        confirmationToken: input.confirmationToken,
      }, providerId),
  );
  const bulkDeleteRenew = useApiMutation((o, input: { confirm: boolean; statusFilter: string[] }) =>
    postMailcomBulkDeleteAndRenew(o, input, providerId),
  );
  const [bulkDeleteStatuses, setBulkDeleteStatuses] = useState<string[]>([...BULK_DELETE_STATUSES]);
  const [bulkDeleteMenuOpen, setBulkDeleteMenuOpen] = useState(false);
  // 2026-09-22 — "Canlandır" (/parents/revive) and "Hatalı Yenile"
  // (/parents/retry-failed-renewals) exist ONLY as mail.com routes
  // (admin-parent-operator-routes.ts); on gmx both answered 404, so they are
  // hidden for every other provider instead of failing on click.
  const mailcomOnlyOps = providerId === 'mailcom';

  const lastBanner =
    // R35.1 (D-151) — BUTTON-1 success text aligned to V1 (ParentMails.jsx:144).
    // bulkRenew's dry-run state (data.dryRun) is skipped so only the
    // post-execute result is shown.
    // 2026-05-28 — workflowFailed sayısı eklendi: önceden sessizdi, operatör
    // 148 fail'i göremedi. Şimdi banner "X başlatıldı, Y atlandı, Z FAIL" gösterir.
    bulkRenew.state.kind === 'ok' && bulkRenew.state.data.dryRun !== true
      ? (() => {
          const d = bulkRenew.state.data;
          const started = d.started ?? 0;
          const skipped = (d.deduplicated ?? 0) + (d.enqueueRejected ?? 0);
          const failed = d.workflowFailed ?? 0;
          const failPart = failed > 0 ? `, ⚠️ ${failed} FAIL (workflow başlatılamadı)` : '';
          // FORCE result: surface wiped + protected counts. The "yeniden
          // üretiliyor" claim is tied to `started` (the parents actually
          // re-enqueued), NOT to the wiped count — so it stays truthful even if
          // some parent could not be re-enqueued.
          const wipePart =
            d.force === true
              ? `, ${(d.wipedAvailable ?? 0) + (d.wipedUsed ?? 0)} alias silindi` +
                (started > 0 ? `, ${started} parentte 9 taze üretiliyor` : '') +
                ((d.protectedActiveOrders ?? 0) > 0 ? `, ${d.protectedActiveOrders} aktif sipariş korundu` : '')
              : '';
          return `${started} parent renewal'a alındı (${skipped} atlandı${failPart}${wipePart})`;
        })()
      : revive.state.kind === 'ok'
        ? `Canlandır: ${revive.state.data.totalCandidates} aday, ${revive.state.data.revivedStatus ?? 0} aktif yapıldı, ${revive.state.data.outboxEnqueued ?? 0} session refresh event.`
        : retryFailed.state.kind === 'ok'
          // R35.1 — V1 (ParentMails.jsx:337) toast shape adapted to V2's
          // job-based retry-failed (V2 has no parent alias-count breakdown;
          // surfaces stale-job total + deduplicated instead).
          ? `${retryFailed.state.data.started ?? 0} parent kuyruğa (${retryFailed.state.data.totalStaleJobs} bayat job), ${retryFailed.state.data.deduplicated ?? 0} atlandı`
          : forceRenew.state.kind === 'ok'
            // mailcom returns a workflowId; the provider-agnostic gmx force-renew
            // has no Temporal start (Python pool claims the enqueued job), so it
            // returns jobId only — fall back to it for a clean toast either way.
            ? `FORCE Renew başladı: ${
                forceRenew.state.data.workflowId
                  ? `workflowId=${forceRenew.state.data.workflowId}`
                  : `jobId=${forceRenew.state.data.jobId}`
              } (${forceRenew.state.data.status})`
            : null;

  async function runBulkRenew(): Promise<void> {
    // R35.1 (D-151) — V1 parity (ParentMails.jsx:132-152): dry-run count →
    // confirm → execute. The backend now starts bootstrap-mode workflows so
    // 0-alias parents are not skipped at 'ineligible'.
    const dry = await bulkRenew.run({ confirm: false });
    if (dry.kind !== 'ok') {
      window.alert('Toplu yenileme dry-run hatası: ' + (dry.kind === 'error' ? dry.reason : dry.kind));
      return;
    }
    const count = dry.data.totalCandidates ?? 0;
    if (count === 0) {
      window.alert('Renewal alınacak parent yok');
      return;
    }
    if (
      !window.confirm(
        `${count} parent için FORCE yenileme başlatılacak (tüm statüler).\n\n` +
          `Her parent'ın available + used alias'ları silinecek ve 9 alias yeniden üretilecek.\n` +
          `Aktif siparişli (leased/reserved) alias'lar KORUNUR — müşteri OTP'si bozulmaz.\n\n` +
          `⚠️ DİKKAT: Alias silip yeniden oluşturmak mail.com'un ömür-boyu ~100 alias yaratma ` +
          `bütçesini (CAP-B) tüketir; tekrarlı kullanım hesapları KALICI olarak devre dışı bırakır.\n\n` +
          `Onaylıyor musunuz?`,
      )
    ) {
      return;
    }
    const r = await bulkRenew.run({ confirm: true });
    if (r.kind === 'ok') onRefreshAll();
    else window.alert('Toplu yenileme hatası: ' + (r.kind === 'error' ? r.reason : r.kind));
  }
  async function runRevive(): Promise<void> {
    if (!window.confirm("Disabled / login_failed parent'lar aktif yapılacak ve session refresh event'i gönderilecek. Devam?")) return;
    const r = await revive.run({ confirm: true });
    if (r.kind === 'ok') onRefreshAll();
  }
  async function runRetryFailed(): Promise<void> {
    // R35.1 (D-151) — V1 parity (ParentMails.jsx:329-346): confirm → execute.
    // Backend starts bootstrap-mode workflows.
    if (!window.confirm("Yenileme hatası ve alias'sız parent'lar renewal sürecine alınacak. Devam etmek istiyor musunuz?")) return;
    const r = await retryFailed.run({ confirm: true });
    if (r.kind === 'ok') onRefreshAll();
    else window.alert('Toplu yenileme başarısız: ' + (r.kind === 'error' ? r.reason : r.kind));
  }
  async function runForceRenew(): Promise<void> {
    const accountId = window.prompt('FORCE Renew için Parent Account ID girin:');
    if (!accountId || accountId.trim().length === 0) return;
    const trimmed = accountId.trim();
    const reason = window.prompt("Neden? (≥4 karakter, audit log'a kaydedilir)");
    if (!reason || reason.trim().length < 4) {
      window.alert('Neden en az 4 karakter olmalı. İptal.');
      return;
    }
    const token = window.prompt(`Onay için Parent Account ID'sini tekrar yazın:\n${trimmed}`);
    if (token !== trimmed) {
      window.alert('Onay token eşleşmedi. İptal.');
      return;
    }
    const r = await forceRenew.run({ accountId: trimmed, reason: reason.trim(), confirmationToken: trimmed });
    if (r.kind === 'ok') onRefreshAll();
  }
  async function handleBulkDeleteAliases(): Promise<void> {
    if (bulkDeleteStatuses.length === 0) {
      window.alert('En az 1 statü seçin');
      return;
    }
    // Step 1 — dry-run count.
    const dry = await bulkDeleteRenew.run({ confirm: false, statusFilter: bulkDeleteStatuses });
    if (dry.kind !== 'ok') {
      window.alert('Dry-run başarısız: ' + (dry.kind === 'error' ? dry.reason : dry.kind));
      return;
    }
    const d = dry.data;
    if (d.affectedAccountCount === 0) {
      window.alert('Bu kriterde parent yok');
      return;
    }
    // Step 2 — confirm (V1 parity warning). H3 FIX: leased/reserved aliases are
    // PROTECTED (skipped), so the warning now says "korunacak" not "etkilenecek".
    const confirmText = [
      `${d.affectedAccountCount} parent etkilenecek.`,
      `${d.affectedAliasCount} alias (available + used) kalıcı olarak silinecek.`,
      d.activeOrdersImpacted > 0
        ? `✓ ${d.activeOrdersImpacted} aktif/rezerve alias KORUNACAK (silinmez) — müşteri OTP'si bozulmaz.`
        : '',
      'Renewal sıfırdan başlayacak. Bu işlem GERİ ALINAMAZ.',
      '⚠️ Alias silip yeniden oluşturmak mail.com ömür-boyu ~100 alias bütçesini (CAP-B) tüketir; tekrarlı kullanım hesapları KALICI devre dışı bırakır.',
      '',
      'Seçili statüler: ' + bulkDeleteStatuses.join(', '),
      '',
      'Devam edilsin mi?',
    ]
      .filter(Boolean)
      .join('\n');
    if (!window.confirm(confirmText)) return;
    // Step 3 — execute (backgrounded).
    const res = await bulkDeleteRenew.run({ confirm: true, statusFilter: bulkDeleteStatuses });
    if (res.kind === 'ok') {
      window.alert(
        `Background başladı: ${res.data.affectedAccountCount} parent / ${res.data.affectedAliasCount} alias işleniyor. Renewal kuyruğa giriyor.`,
      );
      setBulkDeleteMenuOpen(false);
      onRefreshAll();
    } else {
      window.alert('Toplu alias silme hatası: ' + (res.kind === 'error' ? res.reason : res.kind));
    }
  }

  return (
    <Card data-testid="mailcom-v1-action-bar">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <h2 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>Parent Mail Aksiyonları</h2>
        <span className="t-tertiary t-xs">
          {aliasOps
            ? mailcomOnlyOps
              ? 'R23.5 — V1 parity 5 buton (bulk-renew, revive, retry-failed, force-renew, export-and-delete). Arka uç V2-native.'
              : 'Canlandır ve Hatalı Yenile yalnız mail.com içindir; bu sağlayıcıda karşılık gelen sunucu işlemi yok.'
            : 'Alias sistemi yok: mailbox\u2019un kendisi satış birimidir. Yalnız Import / TXT, Statü ve Yenile.'}
        </span>
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {aliasOps ? (
        <>
        <Button
          variant="secondary"
          onClick={runBulkRenew}
          loading={bulkRenew.state.kind === 'running'}
          disabled={!canDrain}
          title={
            !canDrain
              ? 'provider:drain yetkisi yok (yalnız süper admin)'
              : "FORCE wipe+re-add: tüm parent'ların available+used alias'larını siler + 9 yeniden ürettirir (leased/reserved korunur)."
          }
          data-testid="action-bulk-renew"
          icon={<Icon.Refresh size={14} />}
        >
          Toplu Yenileme
        </Button>
        {mailcomOnlyOps ? (
        <>
        <Button
          variant="secondary"
          onClick={runRevive}
          loading={revive.state.kind === 'running'}
          disabled={!canManage}
          title={!canManage ? 'provider:manage_inventory yetkisi yok' : 'Disabled/login_failed parent\'ları aktif yapar + session refresh event\'i gönderir.'}
          data-testid="action-revive"
          icon={<Icon.Sparkle size={14} />}
        >
          Canlandır
        </Button>
        <Button
          variant="secondary"
          onClick={runRetryFailed}
          loading={retryFailed.state.kind === 'running'}
          disabled={!canManage}
          title={!canManage ? 'provider:manage_inventory yetkisi yok' : '1+ saat bayat failed/queued renewal job\'ları requeue + workflow başlatır.'}
          data-testid="action-retry-failed"
          icon={<Icon.Refresh size={14} />}
        >
          Hatalı Yenile
        </Button>
        </>
        ) : null}
        <Button
          variant="secondary"
          onClick={runForceRenew}
          loading={forceRenew.state.kind === 'running'}
          disabled={!canManage}
          title={!canManage ? 'provider:manage_inventory yetkisi yok' : "Tek parent için FORCE Renew. G2 active-order guard bypass EDİLMEZ."}
          data-testid="action-force-renew"
          icon={<Icon.Bolt size={14} />}
        >
          FORCE Renew
        </Button>
        {EXPORT_BUTTON_VISIBLE(activeStatusFilter) ? (
          <Button
            variant="secondary"
            onClick={() => triggerExportAndDelete(onRefreshAll, activeStatusFilter, providerId)}
            disabled={!canDrain}
            title={
              !canDrain
                ? 'provider:drain yetkisi yok (yalnız süper admin)'
                : "Seçili statüdeki TÜM parent'ları TXT olarak indirir, sonra onay karşılığında " +
                  'veritabanımızdan KALICI siler (parent + alias + yenileme + oturum + envanter + kimlik). ' +
                  'Leased aliaslar DA silinir. Sağlayıcıdaki aliaslara dokunulmaz. GERİ ALINAMAZ.'
            }
            data-testid="action-export-delete"
            icon={<Icon.Trash size={14} />}
          >
            Export & Sil
          </Button>
        ) : null}
        <div style={{ position: 'relative', display: 'inline-flex', gap: 8 }}>
          <Button
            variant="secondary"
            onClick={() => setBulkDeleteMenuOpen((v) => !v)}
            disabled={!canDrain}
            title={!canDrain ? 'provider:drain yetkisi yok (yalnız süper admin)' : 'Aliasları silinecek parent statülerini seç.'}
            data-testid="bulk-delete-status-dropdown"
          >
            Statü ({bulkDeleteStatuses.length})
          </Button>
          {bulkDeleteMenuOpen ? (
            <div
              style={{
                // 2026-06-06 — dark-mode readability fix. The old inline tokens
                // (--surface / --border) are NOT defined in styles.css, so they
                // fell back to a hardcoded white #fff panel; with --text-primary
                // (#ecedef) labels that rendered white-on-white (invisible) in
                // dark mode. Switch to the real theme-aware tokens the shared
                // `.menu` class uses (--bg-elevated / --border-default /
                // --shadow-lg / --radius-md) + explicit label color.
                position: 'absolute', top: '100%', left: 0, zIndex: 20, marginTop: 4, minWidth: 210,
                padding: 8, borderRadius: 'var(--radius-md)', background: 'var(--bg-elevated)',
                color: 'var(--text-primary)',
                border: '1px solid var(--border-default)', boxShadow: 'var(--shadow-lg)',
              }}
              data-testid="bulk-delete-status-menu"
            >
              {BULK_DELETE_STATUSES.map((s) => (
                <label
                  key={s}
                  style={{
                    display: 'flex', gap: 8, alignItems: 'center', padding: '4px 6px',
                    cursor: 'pointer', color: 'var(--text-primary)',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={bulkDeleteStatuses.includes(s)}
                    onChange={(e) =>
                      setBulkDeleteStatuses((prev) => (e.target.checked ? [...prev, s] : prev.filter((x) => x !== s)))
                    }
                    // accentColor ties the checked fill to the brand token so
                    // both checked + unchecked states stay visible in dark mode.
                    style={{ accentColor: 'var(--brand-600, #5b84fe)' }}
                    data-testid={`bulk-delete-status-checkbox-${s}`}
                  />
                  <span>{BULK_DELETE_LABELS[s]}</span>
                </label>
              ))}
            </div>
          ) : null}
          <Button
            variant="danger"
            onClick={handleBulkDeleteAliases}
            loading={bulkDeleteRenew.state.kind === 'running'}
            disabled={!canDrain || bulkDeleteStatuses.length === 0}
            title={!canDrain ? 'provider:drain yetkisi yok (yalnız süper admin)' : "Seçili statü parent'larının aliaslarını siler + renewal başlatır (FORCE, geri alınamaz)."}
            data-testid="bulk-delete-aliases-button"
            icon={<Icon.Trash size={14} />}
          >
            Aliasları Sil &amp; Renew
          </Button>
        </div>
        </>
        ) : null}
        <div style={{ flex: 1 }} />
        <Button
          variant="ghost"
          onClick={onRefreshAll}
          title="Özet, parent listesi, envanter ve outbox event'lerini yeniden yükler."
          data-testid="action-status-refresh"
          icon={<Icon.Refresh size={14} />}
        >
          Statü
        </Button>
        <Button
          variant="secondary"
          onClick={onOpenImport}
          disabled={!canManage}
          title={!canManage ? 'provider:manage_inventory yetkisi yok' : 'TXT / bulk import modalını açar.'}
          data-testid="action-import"
          icon={<Icon.ArrowUR size={14} />}
        >
          Import / TXT
        </Button>
        <Button
          variant="ghost"
          onClick={onRefreshAll}
          data-testid="action-refresh"
          icon={<Icon.Refresh size={14} />}
        >
          Yenile
        </Button>
      </div>
      {lastBanner ? (
        <div style={{ marginTop: 12 }}>
          <Banner tone="success" testId="action-last-result">{lastBanner}</Banner>
        </div>
      ) : null}
      {bulkRenew.state.kind === 'error' ? <ErrLine label="Toplu Yenileme" reason={bulkRenew.state.reason} /> : null}
      {revive.state.kind === 'error' ? <ErrLine label="Canlandır" reason={revive.state.reason} /> : null}
      {retryFailed.state.kind === 'error' ? <ErrLine label="Hatalı Yenile" reason={retryFailed.state.reason} /> : null}
      {forceRenew.state.kind === 'error' ? <ErrLine label="FORCE Renew" reason={forceRenew.state.reason} /> : null}
    </Card>
  );
}

function ErrLine({ label, reason }: { readonly label: string; readonly reason: string }): JSX.Element {
  return (
    <div style={{ marginTop: 8 }}>
      <Banner tone="danger">{label}: {reason}</Banner>
    </div>
  );
}
